import { 
    COMPARISON_EXCEL_BUNDLE_PRODUCT_KEY,  
    EXCEL_DEFAULT_SETTINGS,  
    ExcelDirectionTypes, 
    ExcelProductRows 
} from "./consts";
import { consoleErrorMsg, covertImageUrlToBase64, getObjectKeys } from '../../../utils';
import { Cell } from "./types";
import { ComparisonBundleProducts } from "@pes-ui/utils/lib/types/api/comparison-bundle-products-api";
import { getBrandName, ExcelJSService } from "@pes-ui/utils";
import cloneDeep from 'lodash/cloneDeep';

type ImageCacheObject = { // TODO: needs to move this type into types project in next PR
    [key: string]: string;
}

export class ComparatorExcelHandler {

    private isDataReadyForExport: boolean = false;
    private messages;
    private workbook;
    private worksheet;
    private products;
    private productsUIAAS;
    private labels;
    private categories;
    private rowIndex: number;
    private bundledProducts;
    private partNumbers;
    private mappedProducts;
    private dir;
    private imageCacheObj: ImageCacheObject = {};
    private isBundleProductsEnabled: boolean = false;

    constructor(comparisonData, { messages }) {
        this.init(comparisonData, messages);
    } 

    async exportToExcel() {
        try {
            const { excelJS, error} = await ExcelJSService.getExcelJS();
            
            if(error) throw error;
        
            const fileName = EXCEL_DEFAULT_SETTINGS.fileName;
            
            if(this.isDataReadyForExport) return await ExcelJSService.saveExcel(this.workbook, fileName);
            
            this.workbook = new excelJS.Workbook();
        
            this.worksheet = this.workbook.addWorksheet('Sheet0');
            
            this.rowIndex = 1;
        
            this.mappedProducts = this.mapBundleProductToBaseProducts();
        
            this.addHeaderRow();

            await this.addProductDetails();
            
            this.addCategoryCharacteristics();
    
            this.applyFormatting();
            
            await ExcelJSService.saveExcel(this.workbook, fileName);

            this.isDataReadyForExport = true;

        } catch (err) {
            consoleErrorMsg(err, 'exportToExcel while executing addProductDetails');
        }
    
    }
    
    addHeaderRow() {
        const headerRow = this.getRow(this.worksheet, this.rowIndex);
        const partNumberArray = this.partNumbers;
        const headerRowValues = [];
        for(const partNumber of partNumberArray) {
    
            const { bundleProduct, baseProduct, isBundleProduct } = this.getProductDetailsData(partNumber);
            
            const headerRowString = this.getExcelHeaderRowString({
                isBundleProduct, 
                bundleProduct, 
                baseProduct, 
                partNumber
            });
    
            headerRowValues.push(headerRowString);
        }

        if(this.dir === ExcelDirectionTypes.rtl) {
            headerRowValues.push(' ');
        } else {
            headerRowValues.unshift(' ');
        }

        headerRow.values = headerRowValues;
        headerRow.eachCell({ includeEmpty: false }, (cell: Cell) => {
            cell.font = {
                bold: true,
            };
            cell.alignment = {
                horizontal: 'center',
                vertical: 'middle',
                wrapText: true
            };
        });
    }
    
    getExcelHeaderRowString({isBundleProduct, bundleProduct, baseProduct, partNumber}) {
    
        if(!isBundleProduct || !bundleProduct || !baseProduct) return partNumber;
    
        const { productId: baseProductId, quantity: baseProductQuantity } = baseProduct;
        const { productId: bundleProductId, quantity: bundleProductQuantity } = bundleProduct;
    
        return `(${baseProductQuantity}) ${baseProductId} + (${bundleProductQuantity}) ${bundleProductId}`;
    }
    
    getProductDetailsData(partNumber) {
        const{ auxiliaryProduct:bundleProduct, baseProduct } = this.mappedProducts[partNumber]?.bundleProduct;
        const isBundleProduct = Boolean(bundleProduct);
    
        return { bundleProduct, baseProduct, isBundleProduct };
    }
    
    async addProductDetails() {
        const productKeys: Array<string> = getObjectKeys(ExcelProductRows);
        this.rowIndex++;
    
        for (const productKey of productKeys) {
            
            if(productKey === ExcelProductRows.price && getBrandName() !== 'se') return;
            try {
                await this.addProductDetailRow(productKey);
            } catch (err) {
                consoleErrorMsg(err, 'addProductDetails');
            }
        }
    }
    
    async addProductDetailRow (productKey) {

        if(productKey === ExcelProductRows.bundleUrl && !this.isBundleProductsEnabled) return;

        const productRow = this.getRow(this.worksheet, this.rowIndex);
        const productValuesArray: (string | object)[] = [];
    
        for (const [colIndex, partNumber] of this.partNumbers.entries()) {
    
            if (productKey === ExcelProductRows.image) {
                try {
                    await this.addProductImage({ productRow, partNumber, colIndex });
                } catch (err) {
                    consoleErrorMsg(err, 'addProductDetailRow');
                }
            } else {
                const productRowValue = this.addProductValue({ productKey, partNumber });

                productValuesArray.push(productRowValue);
            }
            
        }

        let priceLabel = ' ';
        if(productKey === ExcelProductRows.price) {
            const { 'prp.price.label' : priceCharacteristicKey } = this.messages;
            if(priceCharacteristicKey) priceLabel = priceCharacteristicKey ? priceCharacteristicKey.replace(/\*/g, '') : '';    
        }

        if(this.dir === ExcelDirectionTypes.rtl) {
            productValuesArray.push(priceLabel);
        } else {
            productValuesArray.unshift(priceLabel);
        }

        productRow.values = productValuesArray;
        this.applyUrlStyleFormatting(productRow, productKey);
    
        this.rowIndex++;
    }
    
    async addProductImage({  
        productRow, 
        partNumber, 
        colIndex 
    }) {
        productRow.height = EXCEL_DEFAULT_SETTINGS.productImageRows.height;

        try {
            const { isBundleProduct, bundleProduct } = this.getProductDetailsData(partNumber);
            let image : string = '';
            const normalProductRangeTl = { 
                col: (this.dir === ExcelDirectionTypes.rtl ? EXCEL_DEFAULT_SETTINGS.startingColumn.rtl.index : EXCEL_DEFAULT_SETTINGS.startingColumn.ltr.index) + colIndex + EXCEL_DEFAULT_SETTINGS.products.single.tl.column, 
                row: this.rowIndex - EXCEL_DEFAULT_SETTINGS.startingRow.index + EXCEL_DEFAULT_SETTINGS.products.single.tl.row
            };
            const bundleProductRangeTl = { 
                col: (this.dir === ExcelDirectionTypes.rtl ? EXCEL_DEFAULT_SETTINGS.startingColumn.rtl.index : EXCEL_DEFAULT_SETTINGS.startingColumn.ltr.index) + colIndex + EXCEL_DEFAULT_SETTINGS.products.bundle.tl.column, 
                row: this.rowIndex - EXCEL_DEFAULT_SETTINGS.startingRow.index + EXCEL_DEFAULT_SETTINGS.products.bundle.tl.row
            };
    
            if(isBundleProduct) {
                image = this.getBundleProductImageUrl(bundleProduct);
    
                if(!image) return;
    
                const bundleProductImageRange = {
                    tl : normalProductRangeTl,
                    ext: { width: EXCEL_DEFAULT_SETTINGS.images.width, height: EXCEL_DEFAULT_SETTINGS.images.height },
                };
                await this.addImageToWorksheet({ range: bundleProductImageRange, image });
            } 
            
            image = this.mappedProducts[partNumber]?.image;
            
            if(!image) return;
            const range = {
                tl: isBundleProduct ? bundleProductRangeTl : normalProductRangeTl,
                ext: { width: EXCEL_DEFAULT_SETTINGS.images.width, height: EXCEL_DEFAULT_SETTINGS.images.height },
            };
            await this.addImageToWorksheet({ range, image });
            
        } catch (err) {
            consoleErrorMsg(err, 'addProductImage');
        }
    }
    
    getBundleProductImageUrl(bundleProduct) {
        const skuList = bundleProduct.skuList as Array<ComparisonBundleProducts.BundleSkuListType>;
        return skuList[skuList.length - 1]?.imageBean?.midDimensionUrl;
    }
    
    async addImageToWorksheet({ range, image }) {
        const extName = image.split('.').pop();
        const result = await this.getBase64ImageFromURL(image);
        const imageId = this.workbook.addImage({
            base64: result,
            extension: extName,
        });
        await this.worksheet.addImage(imageId, range);
    }
    
    async getBase64ImageFromURL(url: string) {
        try {
            if(this.imageCacheObj?.url) return this.imageCacheObj.url;

            const response = await fetch(url);
            const blob = await response.blob();
            const imageUrlResultInBase64 = await covertImageUrlToBase64(blob);
            this.imageCacheObj[url] = imageUrlResultInBase64 as string;
            return imageUrlResultInBase64;
        } catch(err) {
            consoleErrorMsg(err, 'toDataURL');
        }
    }
    
    addProductValue ({ 
        productKey, 
        partNumber, 
    }) {
        let productValue;
        switch (productKey) {
            case ExcelProductRows.url: 
                productValue = this.addNormalProductHyperLinkToWorksheet(partNumber); 
                break;
            case ExcelProductRows.price: 
                productValue = this.addPriceCellToWorksheet(partNumber);
                break;
            case ExcelProductRows.name:
                productValue = this.formDescriptionCell({ partNumber, productKey });
                break;
            case ExcelProductRows.bundleUrl: 
                productValue = this.addBundleProductUrlRow(partNumber); 
                break;
        }

        return productValue;
    }
    
    addBundleProductUrlRow(partNumber) {
        const { bundleProduct } = this.getProductDetailsData(partNumber);

        const productUrl = this.formHyperlinkUrl(bundleProduct?.url);
        const productUrlText = `${this.labels?.viewDetailsLabel} - ${bundleProduct?.productId}`;
        return productUrl && productUrlText ? { text: productUrlText, hyperlink: productUrl } : '';
    }
    
    applyUrlStyleFormatting(productRow, productKey?: string) {
        productRow.eachCell({ includeEmpty: false }, (cell: Cell) => {
            cell.alignment = {
                wrapText: true,
                indent: 1,
                vertical: 'top',
            };
    
            if (cell.isHyperlink) cell.alignment = { ...cell.alignment, horizontal: 'center', vertical: 'middle', indent: 0 };

            if (productKey === ExcelProductRows.price || productKey === ExcelProductRows.name) 
                cell.alignment = { ...cell.alignment, horizontal: this.dir === ExcelDirectionTypes.rtl ? 'right' : 'left' };
        });
    }
    
    formDescriptionCell({ partNumber, productKey}) {
        const { bundleProduct, baseProduct, isBundleProduct } = this.getProductDetailsData(partNumber);
    
        if(isBundleProduct && bundleProduct && baseProduct) {
            const baseProductName = baseProduct?.name;
            const baseProductQuantity = baseProduct?.quantity;
            const bundleProductName = bundleProduct?.name;
            const bundleProductQuantity = bundleProduct?.quantity;
            
            if(!baseProductName || !baseProductQuantity || !bundleProductName || !bundleProductQuantity) return;
    
            return `(${baseProductQuantity}) ${baseProductName} + (${bundleProductQuantity}) ${bundleProductName}`;
        } else {
            return this.mappedProducts[partNumber][productKey]
        }
    }
    
    addNormalProductHyperLinkToWorksheet(partNumber) {
        
        const { isBundleProduct, baseProduct } = this.getProductDetailsData(partNumber);
    
        const productUrl = this.formHyperlinkUrl(this.mappedProducts[partNumber]?.url);
        const productUrlText = isBundleProduct ? `${this.labels?.viewDetailsLabel} - ${baseProduct?.productId}` : this.labels?.viewDetailsLabel;
        return productUrl && productUrlText ? { text: productUrlText, hyperlink: productUrl } : ''
    }
    
    formHyperlinkUrl(url: string): string {
        const host: string = window.location.origin;
        if(!url?.includes('https') || !url?.includes('http')) return  host + url;
        return url;
    }

    addPriceCellToWorksheet(partNumber) {
        const productsUIAASObject = this.productsUIAAS.find((uiaasProduct: { commercialReference: string; }) => partNumber === uiaasProduct?.commercialReference);
        
        if (!productsUIAASObject) return;
    
        const { price, currency } = productsUIAASObject;
    
        if (!price || !currency) return;
        
        return `${price} ${currency}`;
    }
    
    addCategoryCharacteristics() {
    
        let characteristicsRowIndex = this.rowIndex;
    
        const categoryArray = this.getCategoriesParameters(this.categories);
    
        categoryArray.forEach((category) => {

            let valueArray = [];
    
            const characteristicValueArray = this.formCharacteristicsData(category);
            
            valueArray = [...valueArray, ...characteristicValueArray];

            const categoryLabel = category?.label;
            
            if(this.dir === ExcelDirectionTypes.rtl) {
                valueArray.push(categoryLabel);
            } else {
                valueArray.unshift(categoryLabel);
            }

            this.formCharacteristicsRowsWithValues({ characteristicsRowIndex, valueArray });
    
            characteristicsRowIndex++;
        });
    }
    
    applyFormatting() {
    
        this.worksheet.columns.forEach((column: { width: number; }) => {
            column.width = EXCEL_DEFAULT_SETTINGS.columns.width;
        });
    
        let column;

        if(this.dir === ExcelDirectionTypes.rtl) {
            column = this.worksheet.getColumn(this.partNumbers.length + 1);
        } else {
            column = this.worksheet.getColumn(1);
        }

        column.eachCell({ includeEmpty: false }, (cell: Cell) => {
            cell.font = {
                bold: true,
            };
        });
    
        this.worksheet.eachRow((row) => {
            row.eachCell((cell: Cell) => {
                if (cell.isHyperlink) cell.style.font = { underline: true };
            });
        });
    }
    
    getRow(worksheet, rowIndex: number) {
        return worksheet.getRow(rowIndex)
    }
    
    getReverseProducts(products: {}) {
        return getObjectKeys(products).reverse();
    }
    
    getCategoriesParameters(categories) {
        const categoryArray = [];
        categories.map((category) => {
            const categoryKeys = getObjectKeys(category.parameters);
            categoryKeys.map((key) => {
                category.parameters[key]['key'] = key;
                categoryArray.push(category.parameters[key]);
            });
        });
    
        return categoryArray;
    }
    
    formCharacteristicsData(category) {
        const characteristicValueArray = [];
        for (const partNumber of this.partNumbers) {
            const characteristicValue = this.mappedProducts[partNumber]?.productCategories[category.key]?.value;
            let characteristicUrl = this.mappedProducts[partNumber]?.productCategories[category.key]?.url;
            const isValueArray = Array.isArray(characteristicValue);
            const isUrlArray = Array.isArray(characteristicUrl);
            if (isUrlArray) {
                (this.mappedProducts[partNumber].productCategories[category.key]?.url as Array<string>).forEach((url: string | null) => {
                    if (url) characteristicUrl = url ? this.formHyperlinkUrl(url) : '';
                });
            }
            const valueBasedOnCondition = isValueArray ? characteristicValue.join('\n') : characteristicValue;
            const characteristicValueObj = characteristicUrl ? { text: valueBasedOnCondition, hyperlink: characteristicUrl, font: { underline: true } } : valueBasedOnCondition;

            if (!characteristicUrl) { characteristicValueArray.push(valueBasedOnCondition) }
            if (characteristicUrl) { characteristicValueArray.push(characteristicValueObj) }
        }
        return characteristicValueArray;
    }
    
    formCharacteristicsRowsWithValues({ characteristicsRowIndex, valueArray }) {
        const characteristicsRow = this.getRow(this.worksheet, characteristicsRowIndex);
        characteristicsRow.values = valueArray;
        characteristicsRow.eachCell({ includeEmpty: false }, (cell: Cell) => {
            cell.alignment = {
                wrapText: true,
                indent: 1,
                vertical: 'top',
                horizontal: this.dir === ExcelDirectionTypes.rtl ? 'right' : 'left'
            };
        });
    }
    
    mapBundleProductToBaseProducts() {
        const productsData = {};
        
        for(const partNumber of this.partNumbers) {
            let bundleProductObject = this.bundledProducts.find((bundleProduct) => bundleProduct.partNumber === partNumber);
            productsData[partNumber] = productsData[partNumber] || cloneDeep(this.products[partNumber]);
            productsData[partNumber][COMPARISON_EXCEL_BUNDLE_PRODUCT_KEY] = bundleProductObject || null;
        }
    
        return productsData;
    }

    reset(comparisonData, {messages}) { 
        this.init(comparisonData, messages);
    }

    init(comparisonData, messages) {
        const { products, categories, labels, productsUIAAS, bundledProducts  } = comparisonData;
        
        this.products = products || [];
        this.categories = categories || [];
        this.labels = labels || {};
        this.productsUIAAS = productsUIAAS || [];
        this.bundledProducts = bundledProducts || [];
        this.messages = messages || {};

        this.partNumbers = this.products ? this.getReverseProducts(this.products) : [];  
        this.isDataReadyForExport = false;
        this.rowIndex = 1;
        this.dir = document.documentElement.dir || 'auto';  
        
        this.isBundleProductsEnabled = this.bundledProducts.some((bundleProduct) => bundleProduct?.auxiliaryProduct);
    }

}